import { lazy } from 'react'
import {
  createRouter,
  createRootRoute,
  createRoute,
  createHashHistory,
  RouterProvider,
  redirect,
} from '@tanstack/react-router'
import { z } from 'zod'

import { LoginPage } from '@/features/auth/pages/LoginPage'
import { Pages } from '@/shared/config/pages'
import { RootLayout } from '@/shared/routing/RootLayout'
import { RouteErrorBoundary } from '@/shared/routing/RouteErrorBoundary'
import { loadNamespace } from '@/i18n'
import { authStore } from '@/features/auth/services/auth-store'
import { guardAuth, guardAlreadyLoggedIn } from '@/shared/auth/guardAuth'

const rootRoute = createRootRoute({ component: RootLayout })

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.LOGIN,
  validateSearch: z.object({
    returnTo: z.string().optional(),
  }),
  beforeLoad: async ({ search }) => {
    guardAlreadyLoggedIn(search.returnTo)
    await Promise.all([
      loadNamespace('login'),
      loadNamespace('reset-password'),
      loadNamespace('auth-common'),
    ])
  },
  component: LoginPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.RESET_PASSWORD,
  validateSearch: z.object({
    confirmationToken: z.string().optional(),
    token: z.string().optional(),
  }),
  beforeLoad: () =>
    Promise.all([
      loadNamespace('reset-password'),
      loadNamespace('auth-common'),
    ]),
  component: lazy(() =>
    import('@/features/auth/pages/ResetPasswordPage').then(m => ({
      default: m.ResetPasswordPage,
    })),
  ),
})

const confirmAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.CONFIRM_ACCOUNT,
  validateSearch: z.object({
    confirmationToken: z.string().optional(),
    token: z.string().optional(),
  }),
  beforeLoad: () =>
    Promise.all([
      loadNamespace('confirm-account'),
      loadNamespace('auth-common'),
    ]),
  component: lazy(() =>
    import('@/features/auth/pages/ConfirmAccountPage').then(m => ({
      default: m.ConfirmAccountPage,
    })),
  ),
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.DASHBOARD,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('dashboard')
  },
  component: lazy(() =>
    import('@/features/dashboard/pages/DashboardPage').then(m => ({
      default: m.DashboardPage,
    })),
  ),
})

const dashboardFinalizersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.DASHBOARD_FINALIZERS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('finalizers-dashboard')
  },
  component: lazy(() =>
    import('@/features/finalizers-dashboard/pages/FinalizersDashboardPage').then(
      m => ({ default: m.FinalizersDashboardPage }),
    ),
  ),
})

const processingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.PROCESSING,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('processing')
  },
  component: lazy(() =>
    import('@/features/processing/pages/ProcessingPage').then(m => ({
      default: m.ProcessingPage,
    })),
  ),
})

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.REPORTS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('reports')
  },
  component: lazy(() =>
    import('@/features/reports/pages/ReportsPage').then(m => ({
      default: m.ReportsPage,
    })),
  ),
})

const blockOrderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.BLOCK_ORDER,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('block-order')
  },
  component: lazy(() =>
    import('@/features/block-order/pages/BlockOrderPage').then(m => ({
      default: m.BlockOrderPage,
    })),
  ),
})

const shipmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.SHIPMENTS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('shipments')
  },
  component: lazy(() =>
    import('@/features/shipments/pages/ShipmentsPage').then(m => ({
      default: m.ShipmentsPage,
    })),
  ),
})

const returnsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.RETURNS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('returns')
  },
  component: lazy(() =>
    import('@/features/returns/pages/ReturnsPage').then(m => ({
      default: m.ReturnsPage,
    })),
  ),
})

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.NOTIFICATIONS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('notifications')
  },
  component: lazy(() =>
    import('@/features/notifications/pages/NotificationsPage').then(m => ({
      default: m.NotificationsPage,
    })),
  ),
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.PROFILE,
  validateSearch: z.object({
    action: z.string().optional(),
  }),
  beforeLoad: async ({ location }) => {
    guardAuth(undefined, location)
    await Promise.all([loadNamespace('profile'), loadNamespace('auth-common')])
  },
  component: lazy(() =>
    import('@/features/profile/pages/ProfilePage').then(m => ({
      default: m.ProfilePage,
    })),
  ),
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.SETTINGS,
  beforeLoad: async () => {
    guardAuth()
    await loadNamespace('settings')
  },
  component: lazy(() =>
    import('@/features/settings/pages/SettingsLayout').then(m => ({
      default: m.SettingsLayout,
    })),
  ),
})

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: Pages.SETTINGS_USERS_INTERNAL })
  },
  component: () => null,
})

const settingsUsersInternalRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'users/internal',
  component: lazy(() =>
    import('@/features/settings/pages/InternalUsersPage').then(m => ({
      default: m.InternalUsersPage,
    })),
  ),
})

const settingsUsersExternalRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'users/external',
  component: lazy(() =>
    import('@/features/settings/pages/ExternalUsersPage').then(m => ({
      default: m.ExternalUsersPage,
    })),
  ),
})

const settingsUsersPermissionsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'users/permissions',
  component: lazy(() =>
    import('@/features/settings/pages/PermissionsPage').then(m => ({
      default: m.PermissionsPage,
    })),
  ),
})

const settingsFulfillmentRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'fulfillment',
  component: lazy(() =>
    import('@/features/settings/pages/FulfillmentPage').then(m => ({
      default: m.FulfillmentPage,
    })),
  ),
})

const settingsFulfillmentMasterBoxesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'fulfillment/master-boxes',
  component: () => null,
})

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Pages.NO_ACCESS,
  beforeLoad: () => loadNamespace('auth-common'),
  component: lazy(() =>
    import('@/features/auth/pages/NoAccessPage').then(m => ({
      default: m.NoAccessPage,
    })),
  ),
})

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: () => {
    if (authStore.isAuthenticated()) {
      throw redirect({ to: Pages.DASHBOARD })
    }
    throw redirect({ to: Pages.LOGIN })
  },
  component: () => null,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  confirmAccountRoute,
  dashboardRoute,
  dashboardFinalizersRoute,
  processingRoute,
  reportsRoute,
  blockOrderRoute,
  shipmentsRoute,
  returnsRoute,
  notificationsRoute,
  profileRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsUsersInternalRoute,
    settingsUsersExternalRoute,
    settingsUsersPermissionsRoute,
    settingsFulfillmentRoute,
    settingsFulfillmentMasterBoxesRoute,
  ]),
  noAccessRoute,
  catchAllRoute,
])

const router = createRouter({
  routeTree,
  history: createHashHistory(),
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => null,
  defaultPendingMs: 1000,
  defaultPendingMinMs: 0,
  defaultErrorComponent: RouteErrorBoundary,
  defaultViewTransition: true,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />
}
